import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, ShoppingBag } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { TextField } from '@shared/ui/primitives/TextField';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { useDebounce } from '@shared/hooks/useDebounce';
import { formatMoney } from '@shared/lib/format';
import { pageCopy } from '@shared/lib/pageCopy';
import { fetchProducts } from './api';

export function Products(): JSX.Element {
  const copy = pageCopy().products;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const navigate = useNavigate();
  const location = useLocation();
  const detailBase = location.pathname.startsWith('/app/') ? '/app/products' : '/products';

  const productsQuery = useQuery({
    queryKey: ['products', debouncedSearch],
    queryFn: () => fetchProducts({ search: debouncedSearch.trim() || undefined }),
  });

  return (
    <Page>
      <div className="mb-4">
        <TextField
          label="Search products"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leadingIcon={<Search className="h-4 w-4" strokeWidth={1.5} />}
        />
      </div>

      {productsQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-3"><Skeleton className="aspect-[3/4]" /><Skeleton className="aspect-[3/4]" /></div>
      ) : productsQuery.isError ? (
        <ErrorState onRetry={() => productsQuery.refetch()} />
      ) : (productsQuery.data ?? []).length === 0 ? (
        <EmptyState
          icon={<ShoppingBag className="h-7 w-7" strokeWidth={1.5} />}
          title={copy.emptyTitle}
          description={copy.emptyDescription}
        />
      ) : (
        <ul className="grid grid-cols-2 gap-3">
          {(productsQuery.data ?? []).map((p) => (
            <li key={p.product_id}>
              <button type="button" onClick={() => navigate(`${detailBase}/${p.product_id}`)} className="text-left w-full">
                <Card noGrain className="overflow-hidden p-0">
                  <div className="aspect-[4/5] bg-sunken">
                    {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div className="p-3">
                    <p className="text-body text-ink truncate font-semibold">{p.name}</p>
                    {p.category ? <p className="text-caption text-ink-muted truncate mt-0.5">{p.category}</p> : null}
                    <div className="mt-2 flex items-baseline justify-between gap-2">
                      <span className="text-body font-display tabular text-ink">{formatMoney(p.price, { currency: p.currency })}</span>
                      {!p.in_stock ? <span className="text-caption text-ink-muted">Out of stock</span> : null}
                    </div>
                  </div>
                </Card>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}
